import { forwardRef, useRef, useCallback } from 'react';
import { useTranslation } from '../../context/LanguageContext';
import './pnl-card.css';

export interface PnlCardData {
  roi: number;
  totalPnlUsd: number;
  solDeployed: number;
  roundsPlayed: number;
  strategyName: string;
  deploymentMode: string;
  oreMined: number;
  costPerOre: number | null;
  sessionDate: string;
  topMinerWins: number;
  motherlodeWins: number;
}

interface PnlCardProps {
  data: PnlCardData;
  onImagesReady?: () => void;
}

export const PnlCard = forwardRef<HTMLDivElement, PnlCardProps>(({ data, onImagesReady }, ref) => {
  const { t } = useTranslation();
  const isNegative = data.totalPnlUsd < 0;
  const profitSign = isNegative ? '-' : '';
  const dollarClass = isNegative ? 'pnl-card__dollar pnl-card__dollar--negative' : 'pnl-card__dollar';
  const isFree = data.costPerOre === null || data.costPerOre === 0;

  const strategyColorMap: Record<string, string> = {
    random: '#60a5fa',
    plus_mining_cost: '#a78bfa',
    round_sniper: '#34d399',
    cheapest_tiles: '#fb923c',
    lotto_miner: '#fbbf24',
    manual_pattern: '#e67e22',
    tile_pattern: '#e67e22',
    hot_cold_tiles: '#0099cc',
  };
  const strategyColor = strategyColorMap[data.deploymentMode] || '#ffffff';

  const bgLoaded = useRef(false);
  const footerLogoLoaded = useRef(false);

  const checkAllLoaded = useCallback(() => {
    if (bgLoaded.current && footerLogoLoaded.current) {
      onImagesReady?.();
    }
  }, [onImagesReady]);

  return (
    <div ref={ref} className="pnl-card">
      {/* Background image */}
      <img
        src="/pnlbg/mm-pnl-bg-1.png"
        alt=""
        className="pnl-card__bg-image"
        crossOrigin="anonymous"
        onLoad={() => { bgLoaded.current = true; checkAllLoaded(); }}
      />

      {/* Content */}
      <div className="pnl-card__content">
        <div className="pnl-card__hero">
          <div className="pnl-card__profit-label">{t('mining.sessions.pnlCardProfit')}</div>
          <div className="pnl-card__profit">
            <span className={dollarClass}>$</span>{profitSign}{Math.abs(data.totalPnlUsd).toFixed(2)}
          </div>
          <div className="pnl-card__cost-per-ore">
            <span className="pnl-card__cost-per-ore-label">{t('mining.sessions.pnlCardCostPerOre')}</span>
            <span className="pnl-card__cost-per-ore-value">
              {isFree ? 'FREE' : <><span className="pnl-card__dollar">$</span>{data.costPerOre!.toFixed(2)}</>}
            </span>
          </div>
        </div>

        <div className="pnl-card__stats">
          <div className="pnl-card__stat">
            <div className="pnl-card__stat-label">{t('mining.sessions.pnlCardStrategy')}</div>
            <div className="pnl-card__stat-value" style={{ color: strategyColor }}>{data.strategyName}</div>
          </div>
          <div className="pnl-card__stat">
            <div className="pnl-card__stat-label">{t('mining.sessions.pnlCardOreMined')}</div>
            <div className="pnl-card__stat-value">{data.oreMined.toFixed(4)}</div>
          </div>
          <div className="pnl-card__stat">
            <div className="pnl-card__stat-label">{t('mining.sessions.pnlCardRounds')}</div>
            <div className="pnl-card__stat-value">{data.roundsPlayed.toLocaleString()}</div>
          </div>
          <div className="pnl-card__stat">
            <div className="pnl-card__stat-label">{t('mining.sessions.pnlCardTMs')}</div>
            <div className="pnl-card__stat-value">{data.topMinerWins}</div>
          </div>
          <div className="pnl-card__stat">
            <div className="pnl-card__stat-label">{t('mining.sessions.pnlCardMLs')}</div>
            <div className="pnl-card__stat-value">{data.motherlodeWins}</div>
          </div>
        </div>

        <div className="pnl-card__footer">
          <img
            src="/brand-assets/Minemore_Logo_Full_Transparent.png"
            alt=""
            className="pnl-card__footer-logo"
            crossOrigin="anonymous"
            onLoad={() => { footerLogoLoaded.current = true; checkAllLoaded(); }}
          />
          <span>Minemore.app</span>
        </div>
      </div>
    </div>
  );
});
